<template>
  <div
    class="fixed top-header left-0 z-10 h-screen w-screen bg-nucleus-lightestBlue"
  >
    <LoadingScreen :loaded="loaded" :message="loadingMessage"></LoadingScreen>

    <ErrorBox
      v-if="errorHeading || errorContent"
      :heading="errorHeading"
      :content="errorContent"
      :fixed="errorFixed"
      @closeError="
        errorHeading = '';
        errorContent = '';
      "
    ></ErrorBox>

    <HeaderShadow></HeaderShadow>
    <div class="p-4">
      <form @submit.prevent="addRemoteUser">
        <div class="flex pb-4">
          <div class="mr-2 w-1/3">
            <router-link to="/local-admin/remote-users">
              <ButtonSecondary
                type="button"
                :name="$t('buttons.cancel')"
                :disabled="loading"
              ></ButtonSecondary>
            </router-link>
          </div>
          <div class="ml-2 w-2/3">
            <Button
              type="submit"
              :name="$t('buttons.addVerify')"
              :disabled="
                !validateEmail(remoteUser.email) ||
                !required(remoteUser.name) ||
                !required(remoteUser.phone) ||
                !validatePhoneNumber(
                  remoteUser.countryCode,
                  remoteUser.phone
                ) ||
                loading
              "
              :loading="loading"
            ></Button>
          </div>
        </div>

        <div
          class="bg-nucleus-white transition-all"
          :class="{ 'opacity-50': loading }"
        >
          <div class="flex items-center px-4 py-6 border-b">
            <div
              class="text-3xl leading-none p-2 mr-6 flex self-center transition bg-nucleus-primaryBlue text-nucleus-white rounded-full icon-remote-user"
            ></div>

            <div class="text-2xl capitalize">
              {{ $t("global.defaults.newRemote") }}
            </div>
          </div>
          <div class="px-4 py-6 overflow-y-auto scroll-box">
            <Input
              id="name"
              v-model:value="remoteUser.name"
              input-type="text"
              :label="$t('inputs.name.label')"
              :max-length="inputMaxLengths.name"
              :placeholder="$t('inputs.name.placeholder')"
              required
            />
            <Input
              id="email"
              v-model:value="remoteUser.email"
              :update:value="validateEmail(remoteUser.email)"
              input-type="text"
              :label="$t('inputs.email.label')"
              :max-length="inputMaxLengths.email"
              :placeholder="$t('inputs.email.placeholder')"
              :error="error.email"
              required
            />
            <div class="flex">
              <div class="mr-2 w-1/4">
                <SelectCountry
                  id="code"
                  v-model:value="remoteUser.countryCode"
                  :label="$t('inputs.code.label')"
                  :options="countryCodes"
                  :options-common="countryCodesCommon"
                />
              </div>
              <div class="ml-2 w-3/4">
                <Input
                  id="phone"
                  v-model:value="remoteUser.phone"
                  :update:value="
                    validatePhoneNumber(
                      remoteUser.countryCode,
                      remoteUser.phone
                    )
                  "
                  input-type="text"
                  :label="$t('inputs.phone.label')"
                  :max-length="inputMaxLengths.phone"
                  :placeholder="$t('inputs.phone.placeholder')"
                  :error="error.phone"
                  required
                />
              </div>
            </div>

            <Input
              id="affiliation"
              v-model:value="remoteUser.affiliation"
              input-type="text"
              :label="$t('inputs.affiliation.label')"
              :max-length="inputMaxLengths.affiliation"
              :placeholder="$t('inputs.affiliation.placeholder')"
            />
            <Textarea
              id="notes"
              v-model:value="remoteUser.notes"
              :label="$t('inputs.notes.label')"
              :max-length="inputMaxLengths.notes"
              :placeholder="$t('inputs.notes.placeholder')"
            />
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import HeaderShadow from "@/components/utils/HeaderShadow.vue"; // @ is an alias to /src
import Button from "@/components/utils/Button.vue"; // @ is an alias to /src
import ButtonSecondary from "@/components/utils/ButtonSecondary.vue"; // @ is an alias to /src
import Input from "@/components/utils/Input.vue"; // @ is an alias to /src
import Textarea from "@/components/utils/Textarea.vue"; // @ is an alias to /src
import Select from "@/components/utils/Select.vue"; // @ is an alias to /src
import SelectCountry from "@/components/utils/SelectCountry.vue"; // @ is an alias to /src
import CountryCodesJson from "@/store/data/countryCodes.json"; // @ is an alias to /src
import CountryCodesCommonJson from "@/store/data/countryCodesCommon.json"; // @ is an alias to /src
import LoadingScreen from "@/components/global/LoadingScreen.vue";
import ErrorBox from "@/components/utils/ErrorBox.vue";

import parsePhoneNumber from "libphonenumber-js";

import InputMaxLengths from "@/store/data/inputMaxLengths.json";
import { getLocalisedError } from "@/utils/apiUtils";

@Options({
  components: {
    Button,
    ButtonSecondary,
    HeaderShadow,
    Input,
    Textarea,
    Select,
    SelectCountry,
    ErrorBox,
    LoadingScreen,
  },
  data() {
    return {
      countryCodes: CountryCodesJson,
      countryCodesCommon: CountryCodesCommonJson,
      remoteUser: {
        name: "",
        email: "",
        countryCode: CountryCodesCommonJson[0].dial_code,
        phone: "",
        affiliation: "",
        notes: "",
      },
      correctNumber: "",
      error: {
        email: "",
        phone: "",
      },
      loading: false,
      loaded: false,
      loadingMessage: this.$t("global.defaults.loadingRemote"),
      errorHeading: "",
      errorContent: "",
      errorFixed: true,
      inputMaxLengths: InputMaxLengths[0],
    };
  },
  mounted() {
    document.title = document.title =
      this.$t("global.defaults.addRemote") +
      " - " +
      this.$t("global.defaults.browserTitle");
    this.loaded = true;
  },
  methods: {
    addRemoteUser() {
      this.loading = true;
      this.loaded = false;

      const data = {
        name: this.remoteUser.name,
        email: this.remoteUser.email,
        phone: this.correctNumber,
        affiliation: this.remoteUser.affiliation,
        notes: this.remoteUser.notes,
        tenantId: this.remoteUser.tenantId,
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.$store
        .dispatch("addRemoteUser", data)
        .then((response: any) => {
          if (response) {
            this.verifyUser(response.data.userId);
            console.log("add remote", response);
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.loading = false;
          this.loaded = true;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    validateEmail(email: string) {
      const mailformat =
        // eslint-disable-next-line no-useless-escape
        /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

      if (this.required(email)) {
        if (!email.match(mailformat)) {
          this.error.email = this.$t("inputs.email.errorInvalid");
          return false;
        } else {
          this.error.email = "";
          return true;
        }
      } else {
        return false;
      }
    },
    validatePhoneNumber(code: any, phone: any) {
      const phoneNumber = parsePhoneNumber(
        code + phone.replace(/^0+/, "").replace(/\s/g, "")
      );

      if (this.required(phone)) {
        if (phoneNumber?.isValid()) {
          this.error.phone = "";
          this.correctNumber = phoneNumber.number;
          return true;
        } else {
          this.error.phone = this.$t("inputs.phone.errorInvalid");
          return false;
        }
      } else {
        return false;
      }
    },
    required(input: string, inputName: string) {
      if (input.length > 0) {
        if (inputName) this.error[inputName] = "";
        return true;
      } else {
        if (inputName)
          this.error[inputName] =
            inputName + " " + this.$t("error.validation.isRequired");
        return false;
      }
    },
    verifyUser(userId: string) {
      this.$store
        .dispatch("inviteRemoteUser", userId)
        .then((response: Record<string, unknown>) => {
          if (response) {
            this.loading = false;
            this.loaded = true;
            this.$store.dispatch("changeUpdate", "remoteUser");
            this.$router.push({
              name: "remoteUsers",
            });
            console.log("add remote", response);
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.loading = false;
          this.loaded = true;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
  },
})
export default class RemoteUsersAdd extends Vue {}
</script>

<style lang="scss" scoped>
.scroll-box {
  height: calc(100vh - 278px);
}
</style>
